import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { AppSettings, AppSettingsSchema, defaultSettings } from './settings.schema';
import { logger } from '@/lib/logger';

const settingsDocRef = (teamId: string) => doc(db, 'teams', teamId, 'settings', 'app');

export async function getTeamSettings(teamId: string): Promise<AppSettings> {
  const snap = await getDoc(settingsDocRef(teamId));
  if (!snap.exists()) return defaultSettings(teamId);
  const result = AppSettingsSchema.safeParse(snap.data());
  if (!result.success) {
    logger.warn('[getTeamSettings] Schema validation failed, using defaults:', result.error.issues);
    return { ...defaultSettings(teamId), ...snap.data() } as AppSettings;
  }
  return result.data;
}

export function watchTeamSettings(teamId: string, cb: (s: AppSettings | null) => void) {
  return onSnapshot(settingsDocRef(teamId), (snap) => {
    if (!snap.exists()) return cb(null);
    const result = AppSettingsSchema.safeParse(snap.data());
    if (!result.success) {
      logger.warn(
        '[watchTeamSettings] Schema validation failed, using defaults:',
        result.error.issues
      );
      cb({ ...defaultSettings(teamId), ...snap.data() } as AppSettings);
      return;
    }
    cb(result.data);
  });
}

export async function upsertTeamSettings(
  teamId: string,
  payload: Partial<AppSettings> & { updatedBy?: string }
) {
  const ref = settingsDocRef(teamId);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    const toWrite: AppSettings = {
      ...defaultSettings(teamId),
      ...payload,
      audit: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: payload.updatedBy,
        updatedBy: payload.updatedBy,
      },
    };
    await setDoc(ref, toWrite, { merge: true });
  } else {
    await updateDoc(ref, {
      ...payload,
      'audit.updatedAt': serverTimestamp(),
      'audit.updatedBy': payload.updatedBy ?? null,
    } as any);
  }
}
